import * as React from 'react';
import styled, { css } from 'styled-components';
import { colors } from '../colors/color';
import { getIconName, iconName } from '../icons/Icons';
import { disabledInputWrapperCss, errorStateInputWrapperCss, defaultInputWrapperCss } from './sharedContainerStyles';
import { h1Styling, h2Styling, h3Styling, h4Styling } from '../typography/Heading';


const inputStyleModes = ['default', 'H1', 'H2', 'H3', 'H4'] as const;
export type InputStyleMode = typeof inputStyleModes[number];

export function textInputPadding(fontSize?:14 | 16): {paddingVertical:number, paddingHorizontal:number} 
{
    return {
        paddingVertical: fontSize === 14 ? 4 : 10,
        paddingHorizontal: fontSize === 14 ? 10 : 15
    }
}

// TODO
// Replace inErrorState with displayState:'default'|'error'
export type TextInputProps = {
    id?: string,
    value: string,
    placeholder?:string,
    autoComplete?: string,
    type?: 'text' | 'password' | 'email',
    disabled?: boolean,
    inErrorState?:boolean,
    tabIndex?: number,
    leadingIcon?:iconName,
    trailingIcon?:iconName,
    trailingIconButton?:iconName,
    dataCy?:string,
    onChange: (value: string) => void;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void,
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void,
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void,
    innerRef?:React.RefObject<HTMLInputElement>,
    styleMode?:InputStyleMode,
    ariaLabel?:string,
    fontSize?: 14 | 16
}

export function TextInput(props: TextInputProps): JSX.Element 
{
    //const [focus, setFocus] = React.useState(false);

    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
        props.onChange(event.target.value);
    }

    function handleFocus(e:React.FocusEvent<HTMLInputElement>)
    {
        //setFocus(true);
        if(props.onFocus)
            props.onFocus(e);
    }

    function handleBlur(e:React.FocusEvent<HTMLInputElement>)
    {
        //setFocus(false);
        if(props.onBlur)
            props.onBlur(e);
    }

    const disabled = props.disabled === undefined ? false : props.disabled;
    const inErrorState = props.inErrorState === undefined ? false : props.inErrorState;

    const activeStyleMode = props.styleMode || 'default';

    const { paddingVertical, paddingHorizontal } = textInputPadding(props.fontSize);

    return (
        <InputAreaWrapper 
            disabled={disabled} 
            inErrorState={inErrorState} 
            fontSize={props.fontSize || 16} 
            paddingVertical={paddingVertical} 
            paddingHorizontal={paddingHorizontal} 
            styleMode={activeStyleMode}>
            
            {props.leadingIcon && 
                <TextInputAreaIconContainer disabled={disabled} styleMode={activeStyleMode}>
                    {getIconName(props.leadingIcon)}
                </TextInputAreaIconContainer>
            }

            <input
                id={props.id}
                value={props.value}
                placeholder={props.placeholder}
                autoComplete={props.autoComplete}
                type={props.type}
                disabled={props.disabled}
                data-cy={props.dataCy}
                tabIndex={props.tabIndex}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={props.onKeyDown}
                ref={props.innerRef}
                aria-label={props.ariaLabel} />
                
            {props.trailingIcon &&
                <TextInputAreaIconContainer disabled={disabled} styleMode={activeStyleMode}>
                    {getIconName(props.trailingIcon)}
                </TextInputAreaIconContainer>
            }
        </InputAreaWrapper>
    );
}

const commonInputWrapper = css<{disabled:boolean, inErrorState:boolean}>`
    background: white;
    display: flex;
    flex-direction: row;
    gap:0;
    border-radius: 4px;

    & > input, textarea 
    {
        font-family: "Noto Sans", sans-serif;
        width:100%;
        line-height: 1.4;
        background: transparent;
        outline: none;
        border:none;
        color:${props => props.disabled
            ? colors.inputState.disabled.text
            : (props.inErrorState 
                ? colors.inputState.error.text 
                : colors.text.normal
            )
        };
    }

    & > textarea {
        resize: none;
        word-wrap:break-word;
    }

    & > input::placeholder{
        color:${colors.text.inactive};
    }

    ${props => props.disabled
        ? disabledInputWrapperCss
        : (props.inErrorState ? errorStateInputWrapperCss : defaultInputWrapperCss)
    }
`;

const defaultInputWrapper = css<{disabled:boolean, inErrorState:boolean, fontSize:number, paddingVertical:number, paddingHorizontal:number}>`
    ${commonInputWrapper};

    
    & > input, textarea {
        font-size: ${props => props.fontSize}px;      
        padding: ${props => props.paddingVertical}px ${props => props.paddingHorizontal}px;
    }
`;

const headerStyleInputWrapper = css<{disabled:boolean, inErrorState:boolean, fontSize:number, paddingVertical:number, paddingHorizontal:number, styleMode:InputStyleMode}>`
    
    & > input, textarea {
        font-size: 16px;
        padding: 10px 5px;
        
        &:first-child{
            padding: 10px 15px;
        }
        ${props => props.styleMode === 'H1' && h1Styling};
        ${props => props.styleMode === 'H2' && h2Styling};
        ${props => props.styleMode === 'H3' && h3Styling};
        ${props => props.styleMode === 'H4' && h4Styling};
    }

    // Explicitly called after to override colors from heading styles
    ${commonInputWrapper}
`;

export const InputAreaWrapper = styled.div<{disabled:boolean, inErrorState:boolean, fontSize:number, paddingVertical:number, paddingHorizontal:number, styleMode:InputStyleMode}>`
    ${props => props.styleMode === 'default' ? defaultInputWrapper : headerStyleInputWrapper}
`;

const defaultIconContainer = css<{disabled:boolean}>`
flex: 0 0 22px;
    height:22px;
    align-self: center;
    justify-self: center;
    &:first-child {
        margin-left:5px;
    }
    &:last-child {
        margin-left:5px;
        margin-right:5px;
    }
    & > svg {
        padding:0px;
        display: block;
        width:22px;
        height:22px;
        fill: ${props => props.disabled ? colors.icon.passive : colors.icon.default};
    }
`;

const headerStyleIconContainer = css<{disabled:boolean}>`
flex: 0 0 22px;
    height:22px;
    align-self: center;
    justify-self: center;
    &:first-child {
        margin-left:5px;
    }
    &:last-child {
        margin-left:5px;
        margin-right:5px;
    }
    & > svg {
        padding:0px;
        display: block;
        width:22px;
        height:22px;
        fill: ${props => props.disabled ? colors.text.light : colors.text.normal};
    }
`;

export const TextInputAreaIconContainer = styled.div<{disabled:boolean, styleMode:InputStyleMode}>`
    ${props => props.styleMode === 'default' ? defaultIconContainer : headerStyleIconContainer}
`;